import { useState } from 'react';
import * as React from 'react';
import { isMobile } from 'react-device-detect';

import Badge from '@/components/elements/Badge';
import RaceStatusLink from '@/components/elements/Link/RaceStatusLink';
import useClock from '@/hooks/date/useClock';
import { Race, RaceProgram } from '@/lib/apollo/types/gen/graphql';
import { getVelodromeByCode } from '@/lib/race/velodrome';
import { BetDataList } from '@/modelTypes';
import { raceSelector } from '@/selectors/race';

import GradeInfo from './GradeInfo';
import {
  Content,
  LinkArea,
  RaceInfoWrapper,
  ToggleIcon,
  Wrapper,
} from './style';
import TimeInfo from './TimeInfo';

interface RaceInfoProps {
  raceProgram: RaceProgram;
  race: Race;
  betDataList: BetDataList;
}

const RaceInfo: React.FC<RaceInfoProps> = React.memo(
  ({ raceProgram, race, betDataList }) => {
    const [isOpen, setIsOpen] = useState<boolean>(true);
    const currentTime = useClock();

    const isShowBadge = (races: Race[]) =>
      races.some(
        _race =>
          Object.keys(betDataList).some(id => _race.id === id) &&
          !raceSelector(race).isCanceled,
      );

    const isShowBadgeMobile = isShowBadge(
      raceProgram.races.filter(_race =>
        raceSelector(_race).marginedOrderDeadline.isAfter(currentTime),
      ),
    );

    return (
      <Wrapper>
        <Content>
          <RaceInfoWrapper>
            <GradeInfo race={race} />
            <TimeInfo
              race={race}
              isSalesSuspended={raceProgram.isSalesSuspended}
            />
          </RaceInfoWrapper>
          {isMobile && (
            <Badge isShow={isShowBadgeMobile} top="-10px" right="-10px">
              <ToggleIcon isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} />
            </Badge>
          )}
        </Content>
        <LinkArea count={raceProgram.races.length} isOpen={isOpen}>
          {raceProgram.races.map((r, i) => (
            <RaceStatusLink
              key={`link-${r}-${i}`}
              raceNum={r.raceNum}
              to={`/${
                getVelodromeByCode(raceProgram.velodromeCode).romanName
              }/race/${race.startTime.format('YYYY-MM-DD')}/${r.raceNum}`}
              isPast={raceSelector(r).marginedOrderDeadline.isBefore(
                currentTime,
              )}
              isCanceled={raceProgram.isCanceled || raceSelector(r).isCanceled}
              isShowBadge={
                raceSelector(r).marginedOrderDeadline.isAfter(currentTime) &&
                isShowBadge([r])
              }
              isSelected={race.raceNum === r.raceNum}
            />
          ))}
        </LinkArea>
      </Wrapper>
    );
  },
);

export default RaceInfo;
